import { ComponentType } from '@angular/cdk/portal/portal';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { InquiryComponent } from 'src/app/components/forms/inquiry/inquiry.component';
import { ServicesComponent } from 'src/app/components/forms/services/services.component';
import { LessonComponent } from 'src/app/components/forms/lesson/lesson.component';
import { GroupLessonComponent } from '../components/forms/group-lesson/group-lesson.component';
import { SchedulesComponent } from '../components/forms/schedules/schedules.component';
import { StudentAccountComponent } from '../components/forms/student-account/student-account.component';

@Injectable({
  providedIn: 'root'
})
export class DialogService {

  constructor(
    public dialog: MatDialog
  ) { }

  /**
   * Utility to open dialogs app wide with a default configuration.
   *
   * @param formName
   * @param dialogConfig
   */
   public openDialog(formName: ComponentType<unknown>, configName?, data?) {
    let dialogConfig = this.dialogConfig(configName, data);
    dialogConfig = this.applyMobileOverrides(dialogConfig);
    dialogConfig = this.addAmtDialogPanelClass(dialogConfig);

    let dialogRef = this.dialog.open(formName, dialogConfig);
    return dialogRef;
  }

  /**
   * Tag every dialog opened via this service with a stable panel class so
   * global CSS can target dialog panes without relying on the :has()
   * pseudo-class (which Safari < 15.4 doesn't support).
   */
  private addAmtDialogPanelClass(config: any): any {
    if (!config) return config;
    const existing = config.panelClass;
    const classes = Array.isArray(existing)
      ? [...existing, 'amt-dialog-pane']
      : (existing ? [existing, 'amt-dialog-pane'] : 'amt-dialog-pane');
    return { ...config, panelClass: classes };
  }

  /**
   * On phone or tablet viewports, force the dialog to fit the screen width
   * and cap its height to the viewport so internal sections (mat-dialog-content
   * and tall mat-dialog-actions grids) scroll inside the dialog instead of
   * overflowing past the bottom of the screen. Detected as a touch-capable
   * device with a viewport ≤ 1024px wide: catches iPhone, iPad, and Android
   * tablets, leaves resized desktop browser windows alone.
   */
  private applyMobileOverrides(config: any): any {
    if (!config || typeof window === 'undefined') {
      return config;
    }
    if (!this.shouldUseMobileDialog()) {
      return config;
    }
    return {
      ...config,
      width: '100%',
      maxWidth: '100vw',
      // dvh = dynamic viewport height: tracks the actual visible viewport on
      // iOS Safari when the URL bar collapses/expands, unlike vh which
      // measures the static maximum.
      maxHeight: '100dvh',
    };
  }

  private shouldUseMobileDialog(): boolean {
    if (typeof window === 'undefined') return false;
    // (pointer: coarse) is true when the primary input is finger-based:
    // phones and tablets, including iPad Pro at 1366px wide. Desktops with
    // optional touchscreens still report (pointer: fine).
    return window.matchMedia('(pointer: coarse)').matches;
  }

  dialogConfig(configName: string = 'default', data?: any) {
    let config: {};

    switch (configName) {
      case 'default': {
        config = {
          disableClose: true,
          height: 'auto',
          width: 640,
          closeOnNavigation: true,
          data: {
            height: "auto",
            closeOnNavigation: true
          }
        }
        return config;
        break;
      }
      case 'defaultLargeWithData': {
        config = {
          disableClose: true,
          height: 'auto',
          width: 880,
          closeOnNavigation: true,
          data: data
        }
        return config;
        break;
      }
      case 'defaultWithData': {
        config = {
          disableClose: true,
          height: 'auto',
          width: 680,
          closeOnNavigation: true,
          data: data
        }
        return config;
        break;
      }
      case 'enrollmentFormWithData': {
        config = {
          disableClose: true,
          height: 'auto',
          width: 980,
          closeOnNavigation: true,
          data: data
        }
        return config;
        break;
      }
      case 'paymentFormWithData': {
        config = {
          disableClose: true,
          height: 'auto',
          width: 800,
          maxWidth: '95vw',
          closeOnNavigation: true,
          data: data
        }
        return config;
        break;
      }
      case 'statusFormWithData': {
        config = {
          disableClose: true,
          height: 'auto',
          width: 630,
          closeOnNavigation: true,
          data: data
        }
        return config;
        break;
      }
      case 'enrollmentListFormWithData': {
        config = {
          disableClose: true,
          height: 'auto',
          width: 680,
          closeOnNavigation: true,
          data: data
        }
        return config;
        break;
      }
      case 'fullScreenCreditCardInput': {
        config = {
          maxWidth: '100vw',
          maxHeight: '100vh',
          height: '100%',
          width: '100%',
          // full-viewport-modal marks panes that truly take the whole screen
          // (styles.css lets them cover the super-admin bar); full-screen-modal
          // is shared with largerScreenDashboard and stays nudged below it.
          panelClass: ['full-screen-modal', 'full-viewport-modal'],
          closeOnNavigation: true,
          data: data
        }
        return config;
        break;
      }
      case 'largerScreenDashboard': {
        config = {
          height: 'auto',
          width: 1400,
          panelClass: 'full-screen-modal',
          closeOnNavigation: true,
          data: data
        }
        return config;
        break;
      }
      case 'defaultXL': {
        config = {
          disableClose: true,
          height: 'auto',
          width: 740,
          closeOnNavigation: true,
          data: data
        }
        return config;
        break;
      }
      case 'defaultXL+': {
        config = {
          disableClose: true,
          height: 'auto',
          width: 760,
          closeOnNavigation: true,
          data: data
        }
        return config;
        break;
      }
      case 'confirm': {
        config = {
          width: '550px',
          height: '400px',
          closeOnNavigation: true,
          disableClose: false,
          data: data
        }
        return config;
        break;
      }
      case 'responsiveWithData': {
        config = {
          disableClose: true,
          height: 'auto',
          // width: '800px',
          // maxWidth: '95vw',
          // maxHeight: '90vh',
          panelClass: 'responsive-modal',
          closeOnNavigation: true,
          data: data
        }
        return config;
        break;
      }
    }
  }

  closeAll() {
    this.dialog.closeAll();
  }

  /**
   * Utility method specifically for confirmation dialogs
   */
  public open(component: ComponentType<unknown>, config?: any) {
    const dialogConfig = config || this.dialogConfig('confirm');
    return this.dialog.open(component, dialogConfig);
  }

}
